import useLocalStorage, { deleteFromStorage } from '@rehooks/local-storage'
import { Alert, Button, Heading, Icon, Link, Theme, useStyles, VFlow } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { useAcessoLotacao } from 'components/auth/useAcessoLotacao'
import useSession from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { PageContent } from 'components/layout/PageContent'
import {
  useAtendimentoAddedSubscription,
  useAtendimentoUpdatedSubscription,
  useListaAtendimentoPollingQuery,
  useListaAtendimentoQuery,
  usePossuiAtendimentosNaoFinalizadosQuery,
  useTipoServicoCheckQuery,
} from 'graphql/hooks.generated'
import {
  ListaAtendimentoQuery,
  OrdemListaAtendimentoEnum,
  PageParams,
  StatusAtendimento,
} from 'graphql/types.generated'
import moment from 'moment'
import qs from 'qs'
import React, { CSSProperties, useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import Permissions from 'types/Permissions'

import { ListaAtendimentoFilter, ListaAtendimentoFilterModel } from './filter/ListaAtendimentoFilter'
import { ListaAtendimentoFilterPopperModel } from './filter/ListaAtendimentoFilterPopperForm'
import { ListaAtendimentoFooter } from './ListaAtendimentoFooter'
import { ListaAtendimentoForm } from './ListaAtendimentoForm'
import { ListaAtendimentoItem } from './ListaAtendimentoItem'

export type AtendimentoModel = ListaAtendimentoQuery['atendimentos']['content'][0]

export interface AtendimentosFilterModel {
  filter: ListaAtendimentoFilterModel
  pageParams: PageParams
  lastUpdate: moment.Moment
}

interface UrlParams {
  cidadaoId: string
}

const somenteNaoFinalizadosNumberOfDaysToCompare = 7
const somenteNaoFinalizadosStartDate = moment().subtract(somenteNaoFinalizadosNumberOfDaysToCompare, 'd')
const somenteNaoFinalizadosEndDate = moment().subtract(1, 'd')

export function ListaAtendimentosView() {
  const { classes } = useStyles(createStyles)
  const handleRejection = useErrorHandler()
  const session = useSession()
  const location = useLocation()
  const { unidadeSaude } = useAcessoLotacao()

  const urlParams: UrlParams = qs.parse(location.search, {
    ignoreQueryPrefix: true,
    parameterLimit: 5,
  })

  const [adicionarMode, setAdicionarMode] = useState(!!urlParams.cidadaoId)

  const [state, setState] = useLocalStorage<AtendimentosFilterModel>(
    `${session.data.acesso.id}/lista-atendimento`,
    ListaAtendimentoDefaultFilter
  )

  const [backupFilterStorage, setBackupFilterStorage] = useLocalStorage<ListaAtendimentoFilterPopperModel>(
    `${session.data.acesso.id}/lista-atendimento-filter-backup`
  )

  const deleteBackupFilterStorage = () => deleteFromStorage(`${session.data.acesso.id}/lista-atendimento-filter-backup`)

  const [alreadySeenAlert, setAlreadySeenAlert] = useLocalStorage<moment.Moment>(
    `${session.data.acesso.id}/lista-atendimento-already-seen-alert`
  )

  const { refetch: refetchPolling } = useListaAtendimentoPollingQuery({
    variables: {
      time: moment(state.lastUpdate).valueOf(),
    },
  })

  const viuHaMaisDias =
    alreadySeenAlert && moment().diff(alreadySeenAlert, 'd') > somenteNaoFinalizadosNumberOfDaysToCompare

  const { data: possuiAtendimentosNaoFinalizados } = usePossuiAtendimentosNaoFinalizadosQuery({
    variables: {
      input: {
        inicio: somenteNaoFinalizadosStartDate,
        fim: somenteNaoFinalizadosEndDate,
      },
    },
    skip: !!alreadySeenAlert && !viuHaMaisDias,
  })

  const { data, refetch } = useListaAtendimentoQuery({
    variables: {
      input: {
        query: state.filter.query,
        ordem: state.filter.ordem,
        somenteMeusAtendimentos: state.filter.somenteMeusAtendimentos,
        statusAtendimento:
          state.filter.somenteNaoFinalizados &&
          (!state.filter.statusAtendimento || state.filter.statusAtendimento.length === 0)
            ? [
                StatusAtendimento.EM_ATENDIMENTO,
                StatusAtendimento.EM_ESCUTA_INICIAL,
                StatusAtendimento.AGUARDANDO_ATENDIMENTO,
              ]
            : state.filter.statusAtendimento || [],
        dataInicio: state.filter.somenteNaoFinalizados
          ? somenteNaoFinalizadosStartDate
          : state.filter.periodo?.startDate,
        dataFim: state.filter.somenteNaoFinalizados ? somenteNaoFinalizadosEndDate : state.filter.periodo?.endDate,
        tiposServico: state.filter.tiposServico?.map((value) => value.id),
        lotacoes: state.filter.lotacoes?.map((value) => value.id),
        equipes: state.filter.equipes?.map((value) => value.id),
        registroTardio: false,
        pageParams: state.pageParams,
      },
    },
  })

  useEffect(() => {
    if (!state.lastUpdate || moment(state.lastUpdate).isBefore(moment(), 'day')) {
      setState({
        ...state,
        filter: {
          ...state.filter,
          ...PeriodoDefault,
        },
        lastUpdate: moment(),
      })
    }
  }, [state, setState])

  const refetchLista = useCallback(() => {
    const update = moment()
    refetch()
      .then(() => {
        setState({
          ...state,
          lastUpdate: update,
        })
      })
      .catch(handleRejection)
  }, [handleRejection, refetch, setState, state])

  useEffect(() => {
    let shouldFetch = true

    const timeout = setInterval(() => {
      if (shouldFetch) {
        shouldFetch = false
        refetchPolling({ time: moment(state.lastUpdate).valueOf() })
          .then(({ data }) => data.atendimentoPolling && refetchLista())
          .finally(() => (shouldFetch = true))
          .catch(handleRejection)
      }
    }, 2 * 1000)
    return () => clearInterval(timeout)
  }, [handleRejection, refetchLista, refetchPolling, state.lastUpdate])

  useAtendimentoAddedSubscription({
    variables: { unidadeSaude: unidadeSaude.id },
    onSubscriptionData: () => refetchLista(),
  })

  useAtendimentoUpdatedSubscription({
    variables: { unidadeSaude: unidadeSaude.id },
    onSubscriptionData: () => refetchLista(),
  })

  // utilizado para cache dos dados exibidos em TipoServicoCheckField.tsx
  useTipoServicoCheckQuery({
    fetchPolicy: 'cache-and-network',
    variables: { input: { pageParams: { sort: ['nome'], size: 100 }, unidadeSaudeSessao: true } },
  })

  const handleAdicionarCidadaoClick = () => {
    setAdicionarMode(!adicionarMode)
  }

  const onChangeFilter = (filter: ListaAtendimentoFilterModel) => {
    setState({
      ...state,
      filter,
      pageParams: {
        ...state.pageParams,
        page: 0,
      },
      lastUpdate: moment(),
    })
  }

  const onChangeFooter = (pageParams: PageParams) => {
    setState({ ...state, pageParams })
  }

  const onClickSomenteAtendimentosNaoFinalizados = () => {
    onChangeFilter({
      ...ListaAtendimentoDefaultFilter.filter,
      periodo: { startDate: undefined, endDate: undefined },
      somenteNaoFinalizados: true,
    })
    setAlreadySeenAlert(moment())
  }

  const onClickManterFiltroAtual = () => {
    setAlreadySeenAlert(moment())
  }

  return (
    <>
      <PageContent fluid type='filled' style={classes.heading}>
        <VFlow>
          <Heading level={1}>Lista de atendimentos</Heading>

          {possuiAtendimentosNaoFinalizados?.possuiAtendimentosNaoFinalizados &&
            (!alreadySeenAlert || (alreadySeenAlert && viuHaMaisDias)) && (
              <Alert type='warning' style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}>
                <strong>Atendimentos não finalizados.</strong> Existem atendimentos dos últimos 7 dias que não foram
                finalizados. Esses atendimentos não serão incluídos no histórico do cidadão ou contabilizados nos
                relatórios. Você pode utilizar o filtro “Ver somente atendimentos não finalizados” para visualizá-los e
                assim poder finalizar esses atendimentos.
                <Link
                  tabIndex={1}
                  onClick={onClickSomenteAtendimentosNaoFinalizados}
                  style={{ fontSize: 'inherit', marginLeft: '0.5rem' }}
                >
                  Ver somente atendimentos não finalizados
                </Link>
                <Link
                  tabIndex={1}
                  onClick={onClickManterFiltroAtual}
                  style={{ fontSize: 'inherit', marginLeft: '0.5rem' }}
                >
                  Finalizar esses atendimentos depois
                </Link>
              </Alert>
            )}

          <CheckPermission permission={Permissions.visualizarListaDeAtendimento.cadastrarEditarEExcluir}>
            <VFlow>
              <Button kind='primary' size='medium' onClick={handleAdicionarCidadaoClick}>
                <Icon icon={adicionarMode ? 'angleUp' : 'angleDown'} style={{ marginRight: '0.5rem' }} />
                {adicionarMode ? 'Cancelar adição' : 'Adicionar cidadão'}
              </Button>
              {adicionarMode && <ListaAtendimentoForm cidadaoId={urlParams.cidadaoId} />}
            </VFlow>
          </CheckPermission>
        </VFlow>
      </PageContent>
      <PageContent fluid type='filled' style={classes.container}>
        <ListaAtendimentoFilter
          filter={state.filter}
          onChangeFilter={onChangeFilter}
          filterBackup={backupFilterStorage}
          updateFilterBackup={setBackupFilterStorage}
          deleteFilterBackup={deleteBackupFilterStorage}
        />

        <VFlow vSpacing={0.25}>
          {data.atendimentos && data.atendimentos.content.length !== 0 ? (
            data.atendimentos.content.map((atend, key) => <ListaAtendimentoItem key={key} atend={atend} />)
          ) : (
            <div style={{ marginTop: '0.5rem' }}> Nenhum resultado encontrado.</div>
          )}
          <ListaAtendimentoFooter
            pageInfo={data.atendimentos?.pageInfo}
            pageParams={state.pageParams}
            onChange={onChangeFooter}
          />
        </VFlow>
      </PageContent>
    </>
  )
}

const PeriodoDefault = {
  periodo: {
    endDate: moment().toDate(),
    startDate: moment().toDate(),
  },
}

export const ListaAtendimentoDefaultFilter = {
  filter: {
    ...PeriodoDefault,
    ordem: OrdemListaAtendimentoEnum.CLASSIFICACAO_RISCO,
    statusAtendimento: [
      StatusAtendimento.AGUARDANDO_ATENDIMENTO,
      StatusAtendimento.EM_ATENDIMENTO,
      StatusAtendimento.EM_ESCUTA_INICIAL,
    ],
    tiposServico: [],
    equipes: [],
    lotacoes: [],
    somenteMeusAtendimentos: false,
    somenteNaoFinalizados: false,
  },
  lastUpdate: moment(),
} as AtendimentosFilterModel

const createStyles = (theme: Theme) => ({
  container: {
    backgroundColor: theme.pallete.gray.c90,
  } as CSSProperties,
  heading: {
    paddingBottom: '1rem',
    flexGrow: 0,
  } as CSSProperties,
})
